import {
    BuildingData,
    Metadata,
    RemoteConstantsModel,
    ResourceData,
    TechTreeComponent,
    TileData,
    UnitData,
} from "../models/remoteConstants.model";
import {RecordSectionKey} from "./branchDiff";
import {
    buildMigrationPackage,
    getSectionRecords,
    identityOf,
    isMetadataSection,
    MigratableRecord,
    RecordMigration,
} from "./migrateRecord";

// Dependency-aware migration planning for the Compare Branches "Add" flow.
// Given a record that exists only on Other and is currently blocked by missing
// dependencies, walk its reference graph in Other and produce an ordered list
// of record migrations (dependencies first) that, applied together, bring the
// record over cleanly. References that don't exist even on Other are reported
// as unresolved — the plan can't fix those automatically.

const METADATA_SECTIONS: RecordSectionKey[] = [
    'unitAttributes', 'buildingAttributes',
    'techActionAttributes', 'techPassiveAttributes', 'rewards',
];
const ENTITY_SECTIONS: RecordSectionKey[] = ['units', 'buildings', 'tiles', 'resources', 'techTree'];

interface DepRef {
    ref: string;
    // Sections (in Other) this reference could live in.
    sections: RecordSectionKey[];
}

// Every identity (id or uid) currently present on a state, across all record
// sections — used to decide whether a reference is already satisfied.
function allIdentities(state: RemoteConstantsModel): Set<string> {
    const s = new Set<string>();
    for (const sec of [...ENTITY_SECTIONS, ...METADATA_SECTIONS]) {
        for (const r of getSectionRecords(state, sec)) {
            s.add(identityOf(r, sec));
            if (isMetadataSection(sec)) s.add((r as Metadata).id);
        }
    }
    return s;
}

// The references a record needs to resolve, tagged with the sections to search.
function structuredDeps(record: MigratableRecord, sectionKey: RecordSectionKey): DepRef[] {
    const deps: DepRef[] = [];
    const meta = (ref?: string) => { if (ref) deps.push({ref, sections: METADATA_SECTIONS}); };
    const entity = (ref?: string, sections: RecordSectionKey[] = ENTITY_SECTIONS) => { if (ref) deps.push({ref, sections}); };

    if (isMetadataSection(sectionKey)) {
        const m = record as Metadata;
        for (const req of m.requires ?? []) deps.push({ref: req, sections: [sectionKey]});
        for (const slot of Object.values(m.entities ?? {})) entity(slot as string);
        // values keys that name an entity are checked by the caller's universe;
        // include them as entity refs so the plan can pull them too.
        for (const key of Object.keys(m.values ?? {})) entity(key);
        return deps;
    }

    switch (sectionKey) {
        case 'units':
            for (const a of (record as UnitData).attributes ?? []) meta(a);
            break;
        case 'buildings': {
            const b = record as BuildingData;
            for (const a of b.attributes ?? []) meta(a);
            for (const u of b.produceableUnits ?? []) entity(u, ['units']);
            for (const tier of b.rewards ?? []) for (const r of tier ?? []) meta(r);
            for (const pr of b.passiveRewards ?? []) {
                const t = pr.trigger as { adjacentTo?: string; containing?: string };
                entity(t.adjacentTo);
                entity(t.containing);
            }
            break;
        }
        case 'tiles': {
            const t = record as TileData;
            entity(t.supportingTile, ['tiles']);
            for (const r of t.resources ?? []) entity(r, ['resources']);
            for (const bld of t.buildings ?? []) entity(bld, ['buildings']);
            break;
        }
        case 'resources':
            for (const a of (record as ResourceData).attributes ?? []) meta(a);
            break;
        case 'techTree': {
            const n = record as TechTreeComponent;
            for (const child of n.children ?? []) entity(child, ['techTree']);
            for (const unlock of n.unlocks ?? []) deps.push({ref: unlock, sections: [...ENTITY_SECTIONS, ...METADATA_SECTIONS]});
            break;
        }
    }
    return deps;
}

function findInOther(
    ref: string,
    sections: RecordSectionKey[],
    targetState: RemoteConstantsModel,
): { sectionKey: RecordSectionKey; record: MigratableRecord } | undefined {
    for (const sec of sections) {
        for (const r of getSectionRecords(targetState, sec)) {
            if (identityOf(r, sec) === ref) return {sectionKey: sec, record: r};
            if (isMetadataSection(sec) && (r as Metadata).id === ref) return {sectionKey: sec, record: r};
        }
    }
    return undefined;
}

export interface MigrationPlan {
    // Ordered dependencies-first; applying in order keeps every reference valid.
    migrations: RecordMigration[];
    // References not found on Other either — the user must author these by hand.
    unresolved: { ref: string; neededBy: string }[];
}

export function planRecordMigration(
    targetRecord: MigratableRecord,
    sectionKey: RecordSectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
): MigrationPlan {
    const present = allIdentities(currentState);
    const planned = new Set<string>();
    const migrations: RecordMigration[] = [];
    const unresolved: { ref: string; neededBy: string }[] = [];
    const visiting = new Set<string>();

    const isSatisfied = (ref: string) => present.has(ref) || planned.has(ref);

    function visit(rec: MigratableRecord, secKey: RecordSectionKey) {
        const id = identityOf(rec, secKey);
        if (planned.has(id) || visiting.has(id)) return;  // already planned / cycle
        visiting.add(id);

        for (const dep of structuredDeps(rec, secKey)) {
            if (isSatisfied(dep.ref)) continue;
            const found = findInOther(dep.ref, dep.sections, targetState);
            if (!found) {
                unresolved.push({ref: dep.ref, neededBy: id});
                continue;
            }
            visit(found.record, found.sectionKey);
        }

        visiting.delete(id);
        // Push after deps so the list is dependency-ordered. Skip records that
        // already exist on Current (only their missing deps need migrating).
        if (!present.has(id) && !planned.has(id)) {
            migrations.push(buildMigrationPackage(rec, secKey, targetState, currentState));
            planned.add(id);
        }
    }

    visit(targetRecord, sectionKey);
    return {migrations, unresolved};
}
